import { isReplaceTextRule } from '@carboncopyco/editor-utils';
import type { FindReplaceRule, ITextReplaceRule } from '@carboncopyco/types';
import { Button } from '@carboncopyco/ui';
import { useBoolean } from 'ahooks';
import { isEqual } from 'lodash-es';
import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { string } from 'yup';
import type { InputTextFieldProps } from './InputTextField';
import { InputTextField } from './InputTextField';
import { TextReplaceAdvancedSettings } from './TextReplaceAdvancedSettings/TextReplaceAdvancedSettings';
import { TextReplaceFormContext } from './TextReplaceFormContext';
import { textReplaceDefaultValues } from './defaultValues';
import { hasSimilarRule } from './utils';

export type TextReplaceFormValues = Pick<
  ITextReplaceRule,
  'oldValue' | 'newValue' | 'options'
>;
const helpLink = 'https://help.demostack.com/en/articles/5919246';

export interface TextReplaceFormProps {
  initialValues?: Partial<ITextReplaceRule>;
  onSubmit: (values: TextReplaceFormValues) => Promise<void>;
  submitText?: string;
  onCancel?: () => void;
  disabled?: boolean;
  onFocus?: (e: React.FocusEvent<HTMLElement>) => void;
  autoFocus?: boolean;
  unlimitedText?: boolean;
  advancedSettings: {
    location: {
      decodedUrl: string;
    };
  };
  rules?: FindReplaceRule[];
  onLearnMoreClick?: (link: string) => void;
}

export const TextReplaceForm = ({
  onSubmit,
  onCancel,
  onFocus,
  initialValues,
  autoFocus,
  unlimitedText,
  submitText,
  rules,
  advancedSettings: advancedSettingsConfig,
}: TextReplaceFormProps) => {
  const validationSchema = useMemo(() => {
    const minChars = unlimitedText ? 1 : 3;
    const maxChars = unlimitedText ? 1000 : 200;

    return string()
      .required()
      .min(minChars, `Must be at least ${minChars} characters`)
      .max(maxChars, `Can contain up to ${maxChars} characters`);
  }, [unlimitedText]);

  const [oldValue, setOldValue] = useState(
    initialValues?.oldValue ?? textReplaceDefaultValues.oldValue
  );
  const [newValue, setNewValue] = useState(
    initialValues?.newValue ?? textReplaceDefaultValues.newValue
  );

  const [oldValueError, setOldValueError] = useState('');
  const [newValueError, setNewValueError] = useState('');
  const [ruleExists, setRuleExists] = useBoolean(false);

  const [advancedSettings, setAdvancedSettings] = useState<
    ITextReplaceRule['options']
  >(initialValues?.options ?? textReplaceDefaultValues.options);

  const [autoFocusOldValue, setAutoFocusOldValue] = useBoolean(true);
  const [autoFocusNewValue, setAutoFocusNewValue] = useBoolean();

  const multilineOptions: InputTextFieldProps['multilineOptions'] = {
    height: 'md',
    isExpandableHeight: true,
  };

  const resetTextReplaceForm = (): void => {
    setOldValue(initialValues?.oldValue ?? textReplaceDefaultValues.oldValue);
    setNewValue(initialValues?.newValue ?? textReplaceDefaultValues.newValue);
    setAdvancedSettings(
      initialValues?.options ?? textReplaceDefaultValues.options
    );
  };

  const validateTextField = async (value) => {
    try {
      await validationSchema.validate(value);
    } catch (e) {
      return e.message;
    }
  };

  const isRuleAlreadyExist = () => {
    const textRules = rules.filter(isReplaceTextRule);
    const testRule: Partial<ITextReplaceRule> = {
      ruleId: initialValues?.ruleId || '',
      oldValue,
      newValue,
      options: advancedSettings,
    };

    return hasSimilarRule(textRules, testRule);
  };

  const handleSubmit = async () => {
    const oldValueError = await validateTextField(oldValue);
    const newValueError = await validateTextField(newValue);

    if (oldValueError || newValueError) {
      setOldValueError(oldValueError);
      setNewValueError(newValueError);
      return;
    }

    if (isRuleAlreadyExist()) {
      setRuleExists.setTrue();
      setOldValueError(
        'You already have a rule for this text. You can always edit it from the Manager.'
      );
      return;
    }

    await onSubmit({
      oldValue,
      newValue,
      options: advancedSettings,
    });
    resetTextReplaceForm();
  };

  const isValid = useMemo(
    () =>
      oldValue &&
      newValue &&
      !(
        initialValues?.oldValue === oldValue &&
        initialValues?.newValue === newValue &&
        isEqual(advancedSettings, initialValues?.options) &&
        !ruleExists
      ),
    [initialValues, oldValue, newValue, advancedSettings, ruleExists]
  );

  const formState = useMemo(
    () => ({
      advancedSettings,
      setAdvancedSettings,
    }),
    [advancedSettings]
  );

  useEffect(() => {
    if (initialValues?.oldValue && initialValues.oldValue !== oldValue) {
      setOldValue(initialValues.oldValue);
    }
  }, [initialValues?.oldValue]);

  useEffect(() => {
    if (!autoFocus) {
      return;
    }

    if (oldValue.length > 0 && !newValue.length) {
      setAutoFocusNewValue.setTrue();
    } else {
      setAutoFocusOldValue.setTrue();
    }
  }, [autoFocus]);

  return (
    <TextReplaceFormContext.Provider value={formState}>
      <form
        className="flex flex-col gap-4"
        aria-label="Find and replace text"
      >
        <div className="flex flex-col gap-3.5">
          <InputTextField
            label="Find"
            name="oldValue"
            value={oldValue}
            onChange={(value) => {
              setOldValue(value);
              setOldValueError('');
            }}
            multiline={unlimitedText}
            multilineOptions={multilineOptions}
            errorMessage={oldValueError}
            autoFocus={autoFocusOldValue}
            onFocus={(event) => {
              onFocus?.(event);
            }}
            linkLabel={ruleExists ? 'Learn More' : ''}
            linkUrl={helpLink}
          />
          <InputTextField
            label="Replace everywhere with"
            name="newValue"
            value={newValue}
            placeholder="Value or {{variable}}"
            onChange={(value) => {
              setNewValue(value);
              setNewValueError('');
            }}
            multiline={unlimitedText}
            multilineOptions={multilineOptions}
            errorMessage={newValueError}
            autoFocus={autoFocusNewValue}
            onFocus={(event) => {
              setAutoFocusNewValue.setFalse();
              onFocus?.(event);
            }}
          />
        </div>
        <TextReplaceAdvancedSettings
          location={advancedSettingsConfig.location}
        />
        <div className="flex justify-end">
          <div className="flex flex-row gap-2.5">
            <Button
              label="Cancel"
              onClick={() => onCancel?.()}
              highContrast
            />
            <Button
              aria-label="Submit"
              label={submitText ?? 'Submit'}
              onClick={handleSubmit}
              disabled={!isValid}
            />
          </div>
        </div>
      </form>
    </TextReplaceFormContext.Provider>
  );
};
